import "@/App.css";
import "@/styles/legal-prose.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { Toaster } from "./components/ui/sonner";
import AppStoreBanner from "./components/AppStoreBanner";
import { installErrorLog } from "./lib/errorLog";
import { pageview } from "./lib/analytics";
import { useNativePush } from "./lib/useNativePush";
import Navbar from "./components/Navbar";
import ErrorBoundary from "./components/ErrorBoundary";
import ImpersonationBanner from "./components/ImpersonationBanner";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import MapView from "./pages/MapView";
import NearMe from "./pages/NearMe";
import EmbedTruckWeek from "./pages/EmbedTruckWeek";
import Dashboard from "./pages/Dashboard";
import SchedulePost from "./pages/SchedulePost";
import ScheduleImport from "./pages/ScheduleImport";
import MarketingKit from "./pages/MarketingKit";
import ChallengeJoin from "./pages/ChallengeJoin";
import AdminProChallenge from "./pages/AdminProChallenge";
import Resources from "./pages/Resources";
import AdminResources from "./pages/AdminResources";
import Admin from "./pages/Admin";
import AdminPreloadedTrucks from "./pages/AdminPreloadedTrucks";
import Onboarding from "./pages/Onboarding";
import OwnerGuide from "./pages/OwnerGuide";
import Upgrade from "./pages/Upgrade";
import FindATruck from "./pages/FindATruck";
// Dynamic per-city landing page. Any row in `expansion_cities` with
// a public status (`live` / `coming_soon`) and a dedicated landing URL
// (i.e. not the homepage) resolves at `/food-trucks/:citySlug`.
// Replaces the retired hand-built AustinComingSoon.jsx.
import CityLanding from "./pages/CityLanding";
import FoodTrucksMarket from "./pages/FoodTrucksMarket";
import PreloadedTruckProfile from "./pages/PreloadedTruckProfile";
import OwnerInquiryDetail from "./pages/OwnerInquiryDetail";
import InquiryStatus from "./pages/InquiryStatus";
import BillingSuccess from "./pages/BillingSuccess";
import TruckProfilePreview from "./pages/admin/TruckProfilePreview";
import ProfileEditorPreview from "./pages/admin/ProfileEditorPreview";
import ResearchEnrichment from "./pages/admin/ResearchEnrichment";
import ExpansionEngine from "./pages/admin/ExpansionEngine";
import PublicProfile from "./pages/PublicProfile";
import PublicProfilesDirectory from "./pages/PublicProfilesDirectory";
import Settings from "./pages/Settings";
import CalendarSync from "./pages/CalendarSync";
import CalendarView from "./pages/CalendarView";
import StaffView from "./pages/StaffView";
import AuthCallback from "./pages/AuthCallback";
import MagicLinkLanding from "./pages/MagicLinkLanding";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import DataDeletion from "./pages/DataDeletion";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Features from "./pages/Features";
import ForFans from "./pages/ForFans";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminOta from "./pages/admin/AdminOta";
import AdminSeoTools from "./pages/admin/AdminSeoTools";
import SiteFooter from "./components/SiteFooter";
import StaleHostBanner from "./components/StaleHostBanner";
import InstallBanner from "./components/InstallBanner";
import { Navigate, useParams } from "react-router-dom";

/** Friendly /truck/:id URL — redirects to the SPA's map view with the
 *  detail sheet pre-opened. The CANONICAL share URL (the one that
 *  unfurls in iMessage/WhatsApp) lives at /api/truck/:id/share on the
 *  backend; this route exists so users typing the friendly URL into
 *  Safari still land in the right place. */
function TruckRedirect() {
  const { id } = useParams();
  return <Navigate to={`/map?truck=${encodeURIComponent(id)}`} replace />;
}

/** Fires a fire-and-forget page-view ping on every route change. The backend
 * dedupes per (path, session, day) so back/forward + multi-tab navigation
 * doesn't inflate counts. Owner/admin self-traffic is filtered server-side. */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    pageview(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    installErrorLog();
  }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <RouteTracker />
          <AppShell />
          <AppStoreBanner />
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

/** AppShell — wraps the routed content with the global chrome
 *  (Navbar / SiteFooter / banners). Suppresses ALL of that chrome on
 *  `/embed/*` routes because those pages live INSIDE iframes on
 *  third-party websites and must be 100% chromeless — otherwise an
 *  owner's website would show our nav bar floating above their layout. */
function AppShell() {
  const location = useLocation();
  const isEmbed = location.pathname.startsWith("/embed/");
  // Staff invite links (`/staff/:token`) are accessed by non-TruckTrackr
  // users via a shared URL — showing them the owner-facing navbar
  // ("Map / Calendar / Dashboard / Sign out") is confusing since they
  // have no account. Treat the staff view as chromeless, same pattern
  // as embed routes.
  const isStaffView = location.pathname.startsWith("/staff/");
  // Unlisted city teaser pages (`/food-trucks/<city>`) are meant to
  // feel like their own standalone "coming soon" surface. Surfacing
  // the TruckTrackr nav at the top would give away the existing site
  // and undercut the "under construction" vibe — so keep it chromeless.
  // SEO market pages (Lancaster pilot) are full-chrome product pages;
  // only the unlisted expansion teasers stay chromeless.
  const SEO_MARKET_PATHS = ["/food-trucks/lancaster-pa", "/food-trucks/outer-banks-nc", "/food-trucks/obx", "/food-trucks/bel-air-md", "/food-trucks/newport-news-va"];
  const normalizedPath = location.pathname.toLowerCase().replace(/\/+$/, "");
  const isCityTeaser = normalizedPath.startsWith("/food-trucks/") && !SEO_MARKET_PATHS.includes(normalizedPath);
  const isChromeless = isEmbed || isStaffView || isCityTeaser;
  // SEO: private/app-only routes must never become Google landing pages.
  // Inject `noindex` for them in one place instead of per-page.
  useEffect(() => {
    const NOINDEX_PREFIXES = ["/admin", "/dashboard", "/login", "/signup", "/settings", "/auth", "/staff", "/billing", "/embed"];
    const isPrivate = NOINDEX_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    if (!isPrivate) return undefined;
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex, nofollow");
    el.setAttribute("data-route-noindex", "1");
    document.head.appendChild(el);
    return () => { el.parentNode && el.parentNode.removeChild(el); };
  }, [location.pathname]);
  // Wires native iOS/APNs + Android/FCM push registration on app launch.
  // No-op on web — Web Push continues to flow through the existing
  // browser-permission hook (see InstallBanner / PushPermissionPrompt).
  useNativePush();
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {!isChromeless && <ImpersonationBanner />}
      {!isChromeless && <StaleHostBanner />}
      {!isChromeless && <Navbar />}
      {!isChromeless && <InstallBanner />}
      <main className="flex-1">
        <ErrorBoundary resetKey={location.pathname}>
        <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/map" element={<MapView />} />
                <Route path="/calendar" element={<CalendarView />} />
                <Route path="/staff/:token" element={<StaffView />} />
                <Route path="/calendar-sync" element={<CalendarSync />} />
                {/* One-tap proximity check — manual foreground geolocation,
                    no background tracking. Lighter-weight entry point than
                    the full MapView for "what's literally next to me?" */}
                <Route path="/nearby" element={<NearMe />} />
                <Route path="/embed/truck/:slug/week" element={<EmbedTruckWeek />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/schedule-post" element={<SchedulePost />} />
                <Route path="/dashboard/schedule-import" element={<ScheduleImport />} />
                <Route path="/dashboard/marketing-kit" element={<MarketingKit />} />
                <Route path="/challenge/join/:token" element={<ChallengeJoin />} />
                <Route path="/admin/pro-challenge" element={<AdminProChallenge />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/:slug" element={<Resources />} />
                <Route path="/admin/resources" element={<AdminResources />} />
                {/* Pro Lab retired June 2026 — old bookmarks land on Post Studio. */}
                <Route path="/dashboard/schedule-post/pro-lab" element={<Navigate to="/dashboard/schedule-post" replace />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/blog" element={<AdminBlog />} />
                <Route path="/admin/ota" element={<AdminOta />} />
                <Route path="/admin/seo" element={<AdminSeoTools />} />
                <Route path="/admin/preloaded-trucks" element={<AdminPreloadedTrucks />} />
                <Route path="/admin/research-enrichment" element={<ResearchEnrichment />} />
                <Route path="/admin/expansion" element={<ExpansionEngine />} />
                {/* Admin-only profile preview routes — gated client-side
                    via `user.is_admin` checks inside each page, AND
                    server-side via require_admin on every endpoint they
                    call. No public navigation links to these. */}
                <Route path="/admin/truck-profile-preview/:truckId" element={<TruckProfilePreview />} />
                <Route path="/admin/profile-editor" element={<ProfileEditorPreview />} />
                {/* Phase B: Pro-owner self-service profile editor — same component
                    as the admin variant, mounted in `mode="owner"` so it talks
                    to /api/owner/* endpoints under ownership-gated auth. */}
                <Route path="/dashboard/profile-editor" element={<ProfileEditorPreview mode="owner" />} />
                {/* Compatibility alias — there were stale links pointing to
                    /dashboard/profile (real bug report May 2026 from owners
                    landing on a blank page). Redirect to the real editor
                    so bookmarks/emails keep working. */}
                <Route path="/dashboard/profile" element={<Navigate to="/dashboard/profile-editor" replace />} />
                {/* Public truck profile — only renders when admin has flipped
                    is_published=true on the draft. 404 page otherwise. */}
                <Route path="/t/:slug" element={<PublicProfile />} />
                {/* Public directory — SEO-friendly index of every published
                    /t/:slug profile. No auth required. */}
                <Route path="/profiles" element={<PublicProfilesDirectory />} />
                <Route path="/find-a-truck" element={<FindATruck />} />
                {/* Dynamic per-city landing — resolves for any public
                    expansion_city with a dedicated landing URL
                    (e.g. /food-trucks/austin, /food-trucks/houston).
                    Replaces the retired hand-built /food-trucks/austin route. */}
                <Route path="/food-trucks/lancaster-pa" element={<FoodTrucksMarket slug="lancaster-pa" />} />
                <Route path="/food-trucks/outer-banks-nc" element={<FoodTrucksMarket slug="outer-banks-nc" />} />
                <Route path="/food-trucks/obx" element={<Navigate to="/food-trucks/outer-banks-nc" replace />} />
                <Route path="/food-trucks/bel-air-md" element={<FoodTrucksMarket slug="bel-air-md" />} />
                <Route path="/food-trucks/newport-news-va" element={<FoodTrucksMarket slug="newport-news-va" />} />
                <Route path="/food-trucks/:citySlug" element={<CityLanding />} />
                {/* Preloaded-truck public profile — SEO-indexable on purpose.
                    Lives under `/food-trucks/<city>/<slug>` so per-city
                    Google queries surface our stub before the truck claims. */}
                <Route path="/food-trucks/:city/:slug" element={<PreloadedTruckProfile />} />
                <Route path="/dashboard/inquiries/:recipientId" element={<OwnerInquiryDetail />} />
                {/* Phase 3 — host self-service status page. Deep-linked
                    from the FindATruck confirmation email + the success
                    step on /find-a-truck. Token-gated, no auth required.
                    This is the conversion-loop touchpoint that pulls
                    hosts back to the platform and gets them to share
                    the link with co-organizers. */}
                <Route path="/inquiry/:inquiryId" element={<InquiryStatus />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/onboarding/:token" element={<Onboarding />} />
                <Route path="/owner-guide" element={<OwnerGuide />} />
                <Route path="/upgrade" element={<Upgrade />} />
                {/* /billing/success — Stripe redirects here after a
                    successful checkout. Without this route the SPA
                    catch-all bounced users back to "/" and they thought
                    the payment crashed. Fixes Feb 2026 bug report. */}
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/truck/:id" element={<TruckRedirect />} />
                {/* Soft account gate — Google OAuth callback + magic link */}
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/magic" element={<MagicLinkLanding />} />
                {/* Legal / Google OAuth verification pages — linked from
                    the site footer so reviewers can find them from any
                    public page. */}
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/category/:categorySlug" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/about" element={<About />} />
                <Route path="/features" element={<Features />} />
                <Route path="/customers" element={<ForFans />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                {/* Catch-all — anything we don't route gets a friendly
                    redirect to the landing page instead of a blank
                    screen. Real bug report May 2026: stale links to
                    /dashboard/profile dead-ended owners with a literal
                    blank page. Never again. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
        </ErrorBoundary>
            </main>
            {!isChromeless && <SiteFooter />}
          </div>
  );
}

export default App;
